import { NumberField } from '~/common/components';
import { usePurchaseContext } from '~/purchase/context';
import { Trans, useI18n, useLocale } from '~/common/context';
import { Button, Flex, FormFeedback, Icon, NumberInput, Text } from '@wyz/ui';
import { DEFAULT_EURO_SIGN, invariant, useOnMount } from '@wyz/utils';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import {
  IRimOptionItem,
  ITyreCountOptionItem,
  TranslationKeys,
} from '~/common/types';
import {
  DiameterField,
  useQuoteServicePricingPolicyForm,
} from '~/purchase/components';
import {
  CommonPricingPolicyHelpers,
  getQuoteServicePriceTypeTranslation,
  QuoteServicePricingPolicyHelpers,
} from '~/purchase/helpers';
import { IDiameter, IQuoteServiceMountingStepForm } from '~/purchase/types';

import { get } from 'lodash-es';
import { useState } from 'react';
// eslint-disable-next-line @wyz/module/no-relative-import
import './refinement-table.scss';

type RefinementRowProps = {
  refinementIndex: number;
  refinementFlags: IQuoteServiceMountingStepForm['refinementFlags'];
  rim: IRimOptionItem;
  tyreCountItem: ITyreCountOptionItem;
  onRemoveRow: (index: number) => void;
  isMainRow: boolean;
};

const ServiceRefinementRow = ({
  refinementIndex,
  refinementFlags,
  rim,
  tyreCountItem,
  onRemoveRow,
  isMainRow,
}: RefinementRowProps) => {
  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  const rowBaseName =
    `rims.${rim.value}.refinements.${refinementIndex}` as const;
  const baseName = `${rowBaseName}.tyreCount.${tyreCountItem.value}` as const;
  const methods = useFormContext<IQuoteServiceMountingStepForm>();
  const selectedVehicleTypeGroup = useWatch({ name: 'vehicleTypeGroup' }) as
    | string
    | undefined;
  invariant(selectedVehicleTypeGroup);
  const [defaultValue, setDefaultValue] = useState<number | ''>('');
  const { state } = usePurchaseContext();
  const locale = useLocale();
  const currencySymbol = state.targetCustomer?.deliveryInformation?.country
    .currencyIsoCode
    ? locale.formatters.currency(
        state.targetCustomer?.deliveryInformation?.country.currencyIsoCode,
      )
    : DEFAULT_EURO_SIGN;
  const { t } = useI18n();

  const watchedPriceType = useWatch({
    name: 'priceType',
    control: methods.control,
  });

  return (
    <tr>
      <td>
        {isMainRow && (
          <>
            {refinementFlags.diameter ? (
              <DiameterField
                refinementIndex={refinementIndex}
                baseName={rowBaseName}
                onRemoveRow={onRemoveRow}
                mode={pricingPolicyForm.mode}
              />
            ) : (
              <span>
                <Trans i18nKey='app.quote.pricing_policy.form.step1.all_diameters' />
              </span>
            )}
          </>
        )}
      </td>
      {refinementFlags.tyreCount && <td>{tyreCountItem.label}</td>}
      {refinementFlags.tyreType && (
        <td>
          {pricingPolicyForm.mode === 'WRITE' && (
            <NumberInput
              name={`defaultValue.${rim.value}.${tyreCountItem.value}.${refinementIndex}`}
              withKnobs={false}
              size='sm'
              value={defaultValue as number}
              onChange={({ target }) => {
                const value = Number(target.value);
                setDefaultValue(value);
                QuoteServicePricingPolicyHelpers.getTyreTypeColumns(
                  t,
                  pricingPolicyForm.vehicleTypeGroups.data?.data ?? [],
                  selectedVehicleTypeGroup,
                  refinementFlags.tyreType,
                  true,
                ).forEach((tyreType) => {
                  methods.setValue(
                    `${baseName}.tyreTypes.${tyreType.value}.value`,
                    value,
                    {
                      shouldValidate: true,
                    },
                  );
                });
              }}
              placeholder='xx'
              sign={currencySymbol}
            />
          )}
        </td>
      )}
      {QuoteServicePricingPolicyHelpers.getTyreTypeColumns(
        t,
        pricingPolicyForm.vehicleTypeGroups.data?.data ?? [],
        selectedVehicleTypeGroup,
        refinementFlags.tyreType,
        true,
      ).map((tyreType) => (
        <td key={tyreType.value}>
          <Flex
            alignContent='center'
            justifyContent='start'
            className='space-x-2'
          >
            <div className='max-w-50'>
              <NumberField
                name={`${baseName}.tyreTypes.${tyreType.value}.value`}
                withKnobs={false}
                size='sm'
                placeholder='xx'
                feedbackType='tooltip'
                sign={currencySymbol}
                onChangeValue={() => setDefaultValue('')}
              />
            </div>

            <Text
              color='secondary-gray'
              weight='bold'
              heading='xs'
              className='p-2'
            >
              <Trans
                i18nKey={getQuoteServicePriceTypeTranslation(watchedPriceType)}
              />
            </Text>
          </Flex>
        </td>
      ))}
    </tr>
  );
};

const ServiceRefinementTableBody = ({
  refinementFlags,
  rim,
  rows = [],
  onRemove,
}: {
  onRemove: (idx: number) => void;
  refinementFlags: IQuoteServiceMountingStepForm['refinementFlags'];
  rim: IRimOptionItem;
  rows: NonNullable<
    NonNullable<IQuoteServiceMountingStepForm['rims']>[string]['refinements']
  >;
}) => {
  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  const selectedVehicleTypeGroup = useWatch({ name: 'vehicleTypeGroup' }) as
    | string
    | undefined;
  invariant(selectedVehicleTypeGroup);

  return (
    <tbody>
      {rows.map((_, refinementIndex) => {
        if (refinementFlags.tyreCount) {
          // we'll only have more than one main row in case of diameter refinement
          if (!refinementFlags.diameter && refinementIndex !== 0) return;
          return QuoteServicePricingPolicyHelpers.getTyreCountColumns(
            pricingPolicyForm.tyreCounts.optionItems,
            refinementFlags.tyreCount,
          ).map((tyreCountItem, tyreCountItemIdx) => {
            return (
              <ServiceRefinementRow
                key={`${rim.value}.${tyreCountItem.value}.${refinementIndex}`}
                refinementIndex={refinementIndex}
                refinementFlags={refinementFlags}
                rim={rim}
                tyreCountItem={tyreCountItem}
                onRemoveRow={onRemove}
                isMainRow={tyreCountItemIdx === 0}
              />
            );
          });
        }
        return (
          <ServiceRefinementRow
            key={`${rim.value}.${CommonPricingPolicyHelpers.NO_REFINEMENT_VALUE}.${refinementIndex}`}
            refinementIndex={refinementIndex}
            refinementFlags={refinementFlags}
            rim={rim}
            tyreCountItem={{
              label: CommonPricingPolicyHelpers.NO_REFINEMENT_VALUE,
              value: CommonPricingPolicyHelpers.NO_REFINEMENT_VALUE,
            }}
            onRemoveRow={onRemove}
            isMainRow
          />
        );
      })}
    </tbody>
  );
};

const RefinementSection = ({
  rim,
}: React.PropsWithChildren<{ rim: IRimOptionItem }>) => {
  const arrayMethods = useFieldArray<
    IQuoteServiceMountingStepForm,
    `rims.${string}.refinements`,
    'key'
  >({
    name: `rims.${rim.value}.refinements`,
  });
  const watchedRefinementFlags = useWatch({
    name: 'refinementFlags',
  });

  useOnMount(() => {
    if (arrayMethods.fields.length === 0) {
      arrayMethods.append({ tyreCount: {} });
    }
  });
  return (
    <ServiceRefinementTable>
      <ServiceRefinementTableHeader
        flags={watchedRefinementFlags}
        onAdd={arrayMethods.append}
        rim={rim}
      />
      <ServiceRefinementTableBody
        refinementFlags={watchedRefinementFlags}
        rows={arrayMethods.fields}
        onRemove={arrayMethods.remove}
        rim={rim}
      />
    </ServiceRefinementTable>
  );
};
const ServiceRefinementTable = ({ children }: React.PropsWithChildren) => {
  return <table className='service-refinement-table'>{children}</table>;
};

const ServiceRefinementTableHeader = ({
  flags,
  onAdd,
  rim,
}: {
  flags: IQuoteServiceMountingStepForm['refinementFlags'];
  onAdd: (
    item: NonNullable<
      NonNullable<IQuoteServiceMountingStepForm['rims']>[string]['refinements']
    >[number],
  ) => void;
  rim: IRimOptionItem;
}) => {
  const { t } = useI18n();
  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  const selectedVehicleTypeGroup = useWatch({ name: 'vehicleTypeGroup' }) as
    | string
    | undefined;
  invariant(selectedVehicleTypeGroup);
  const methods = useFormContext<IQuoteServiceMountingStepForm>();

  const diameterError = get(
    methods.formState.errors,
    `rims.${rim.value}.refinements.root.message`,
  ) as keyof TranslationKeys | undefined;

  const watchedPriceType = useWatch({
    name: 'priceType',
    control: methods.control,
  });

  return (
    <thead>
      <tr>
        <th style={{ width: '30%' }}>
          <Flex justifyContent='between' alignItems='center'>
            <div>
              <span>
                <Trans i18nKey='app.quote.pricing_policy.form.step1.diameter_label' />
              </span>
            </div>
            <span>
              {pricingPolicyForm.mode === 'WRITE' && flags.diameter && (
                <Button
                  id='add-diameter'
                  variant='link'
                  size='sm'
                  onClick={() => {
                    const selected =
                      CommonPricingPolicyHelpers.extractSelectedDiameters(
                        methods.getValues(`rims.${rim.value}.refinements`),
                      );
                    const guess = CommonPricingPolicyHelpers.guessNextDiameter(
                      selected.diameters,
                      selected.operators,
                    );
                    onAdd({
                      tyreCount: {},
                      diameter: {
                        from: guess.diameter,
                        operator: guess.operator,
                      } as unknown as IDiameter,
                    });
                  }}
                >
                  <Icon icon='plus' size='sm' />
                  <Trans i18nKey='app.pricing_policy.form.diameter_add_slice' />
                </Button>
              )}
            </span>
          </Flex>
          {diameterError && (
            <FormFeedback invalid id={`rims.${rim.value}.refinements.error`}>
              <Trans i18nKey={diameterError} />
            </FormFeedback>
          )}
        </th>
        {flags.tyreCount && (
          <th style={{ width: '15%' }}>
            <Trans i18nKey='app.common.product.tyres' />
          </th>
        )}
        {flags.tyreType && (
          <th style={{ width: '10%' }}>
            <Trans
              i18nKey={getQuoteServicePriceTypeTranslation(
                watchedPriceType,
                'unit',
              )}
            />
          </th>
        )}
        {QuoteServicePricingPolicyHelpers.getTyreTypeColumns(
          t,
          pricingPolicyForm.vehicleTypeGroups.data?.data ?? [],
          selectedVehicleTypeGroup,
          flags.tyreType,
          true,
        ).map((tyreType) => (
          <th key={tyreType.value}>
            {flags.tyreType
              ? tyreType.label
              : t(
                  getQuoteServicePriceTypeTranslation(watchedPriceType, 'unit'),
                )}
          </th>
        ))}
      </tr>
    </thead>
  );
};

export default RefinementSection;
